'use client';

import { useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type IpVersion = 'ipv4' | 'ipv6' | 'both';
type Ipv4Scope = 'any' | 'private' | 'public';

function randomInt(maxExclusive: number): number {
  return Math.floor(Math.random() * maxExclusive);
}

function randomPublicOctet0(): number {
  // Avoid 0, 10, 127, 169, 172, 192, and 224+ ranges for a "public-ish" first octet.
  const blocked = new Set([0, 10, 127, 169, 172, 192]);
  let value = 1 + randomInt(223); // 1..223
  while (blocked.has(value)) {
    value = 1 + randomInt(223);
  }
  return value;
}

function generateIpv4(scope: Ipv4Scope): string {
  if (scope === 'private') {
    const block = randomInt(3);
    if (block === 0) {
      return `10.${randomInt(256)}.${randomInt(256)}.${randomInt(256)}`;
    }
    if (block === 1) {
      return `172.${16 + randomInt(16)}.${randomInt(256)}.${randomInt(256)}`;
    }
    return `192.168.${randomInt(256)}.${randomInt(256)}`;
  }
  if (scope === 'public') {
    return `${randomPublicOctet0()}.${randomInt(256)}.${randomInt(256)}.${randomInt(256)}`;
  }
  return `${randomInt(256)}.${randomInt(256)}.${randomInt(256)}.${randomInt(256)}`;
}

function randomHextet(): string {
  return randomInt(0x10000).toString(16).padStart(4, '0');
}

function generateIpv6(): string {
  return Array.from({ length: 8 }, randomHextet).join(':');
}

export default function BrowserRandomIpGenerator() {
  const [count, setCount] = useState(10);
  const [version, setVersion] = useState<IpVersion>('ipv4');
  const [scope, setScope] = useState<Ipv4Scope>('any');
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleGenerate() {
    setCopyMessage(null);
    const safeCount = Math.max(1, Math.min(5000, Math.floor(count) || 0));
    if (safeCount < 1) {
      setErrorMessage('Enter how many addresses to generate (1 to 5000).');
      setOutput('');
      return;
    }
    setErrorMessage(null);
    setCount(safeCount);

    const rows: string[] = [];
    for (let i = 0; i < safeCount; i += 1) {
      if (version === 'ipv4') {
        rows.push(generateIpv4(scope));
      } else if (version === 'ipv6') {
        rows.push(generateIpv6());
      } else {
        rows.push(i % 2 === 0 ? generateIpv4(scope) : generateIpv6());
      }
    }
    setOutput(rows.join('\n'));
  }

  async function handleCopy() {
    if (!output) {
      return;
    }
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the addresses.');
    }
  }

  const lineCount = output ? output.split('\n').length : 0;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">IP addresses</span>
        <h2 className="tool-live-title">Generate random IP addresses</h2>
        <p className="tool-live-subtitle">
          Build a list of random IPv4 or IPv6 addresses for tests, mock data, and sample logs. Runs
          fully in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>How many</span>
              <input
                min="1"
                max="5000"
                onChange={(e) => setCount(Number(e.target.value))}
                type="number"
                value={count}
              />
            </label>
            <label className="tool-live-field">
              <span>Version</span>
              <select onChange={(e) => setVersion(e.target.value as IpVersion)} value={version}>
                <option value="ipv4">IPv4</option>
                <option value="ipv6">IPv6</option>
                <option value="both">IPv4 and IPv6</option>
              </select>
            </label>
            {version !== 'ipv6' ? (
              <label className="tool-live-field">
                <span>IPv4 range</span>
                <select onChange={(e) => setScope(e.target.value as Ipv4Scope)} value={scope}>
                  <option value="any">Any address</option>
                  <option value="private">Private ranges only</option>
                  <option value="public">Public only</option>
                </select>
              </label>
            ) : null}
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleGenerate} type="button">
              Generate
            </button>
            <button
              className="button button-secondary"
              disabled={!output}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {errorMessage ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {errorMessage}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Addresses{lineCount ? ` (${lineCount})` : ''}</span>
            <textarea
              placeholder="Press Generate to build a list of random IP addresses."
              readOnly
              rows={16}
              value={output}
            />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
